import { App, Button, Card, Flex, Space, Tabs, Tag, Typography } from 'antd'
import { Key, Plus, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Loading } from 'src/components'
import { useHasWorkspacePermissions, useListQueryState } from 'src/hooks'
import { useShowApiErrors, WorkspacePermissionCode } from 'src/utils'

import { ArchiveRoleModal } from '../../components/ArchiveRoleModal'
import { WorkspacePageShell, useWorkspaceRoute } from '../WorkspacePageShell'

import { isOwnerRole } from './constants'
import {
  useWorkspacePermissions,
  useWorkspaceRole,
  useWorkspaceRoles
} from './hooks'
import { PermissionsCatalogTab } from './PermissionsCatalogTab'
import { RoleDrawer } from './RoleDrawer'
import { RolesKpiChips } from './RolesKpiChips'
import type { EditableWorkspaceRole, RoleFormValues } from './types'
import {
  WorkspaceRolesMatrix,
  type RolePermissionUpdate
} from './WorkspaceRolesMatrix'

const TAB_VALUES = ['roles', 'permissions'] as const
type TabKey = (typeof TAB_VALUES)[number]

const TAB_SCHEMA = {
  tab: { type: 'enum', values: TAB_VALUES, default: 'roles' }
} as const

type RoleDrawerState =
  | { open: false }
  | { open: true; mode: 'create' }
  | { open: true; mode: 'edit'; role: EditableWorkspaceRole }

const TabCountTag = ({ value }: { value: number }) => (
  <Tag style={{ marginInlineEnd: 0, marginInlineStart: 4, fontSize: 10 }}>
    {value}
  </Tag>
)

const RolesContent = () => {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const { workspaceId, idValid } = useWorkspaceRoute()

  const queryState = useListQueryState(TAB_SCHEMA)
  const tab = queryState.values.tab as TabKey

  const canManageRoles = useHasWorkspacePermissions(
    WorkspacePermissionCode.ROLES_MANAGE
  )

  const {
    roles,
    createRole,
    updateRole,
    updateRolePermissions,
    deleteRole,
    replaceAndDelete,
    isLoading: isLoadingRoles,
    isMutating
  } = useWorkspaceRoles(idValid ? workspaceId : null)

  const { permissions, isLoading: isLoadingPermissions } =
    useWorkspacePermissions(idValid ? workspaceId : null)

  const [roleDrawer, setRoleDrawer] = useState<RoleDrawerState>({ open: false })
  const [replaceTarget, setReplaceTarget] =
    useState<EditableWorkspaceRole | null>(null)

  // The list shape carries `users_count`, but the reassignment warning is
  // rendered from the detail so a stale count never understates the impact.
  // Until that detail lands the list value stands in — falling back to zero
  // read as "nobody holds this role" while the request was still in flight.
  const { data: replaceTargetDetail } = useWorkspaceRole(
    replaceTarget?.id ?? null
  )

  const customRoleCount = roles.filter(
    (r) => !isOwnerRole(r.codename) && !r.is_admin && !r.is_system
  ).length

  const replacementCandidates = replaceTarget
    ? roles.filter((r) => r.id !== replaceTarget.id && !isOwnerRole(r.codename))
    : []

  const handleSubmitRole = async (values: RoleFormValues) => {
    try {
      if (roleDrawer.open && roleDrawer.mode === 'edit') {
        await updateRole(roleDrawer.role.id, values)
        void message.success(t('workspace.orgRoles.messages.updated'))
      } else {
        await createRole(values)
        void message.success(t('workspace.orgRoles.messages.created'))
      }
      setRoleDrawer({ open: false })
    } catch (error) {
      showApiErrors(error)
    }
  }

  const handleDeleteRole = (role: EditableWorkspaceRole) => {
    if (isOwnerRole(role.codename)) return

    // A role still held by members can't just be dropped — offer the
    // reassignment flow instead of a delete that would strand them.
    if ((role.users_count ?? 0) > 0) {
      setReplaceTarget(role)

      return
    }

    modal.confirm({
      title: t('workspace.orgRoles.deleteConfirmTitle', { name: role.name }),
      content: t('workspace.orgRoles.deleteConfirmDescription'),
      okText: t('workspace.orgRoles.actions.delete'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteRole(role.id)
          void message.success(t('workspace.orgRoles.messages.deleted'))
        } catch (error) {
          showApiErrors(error)
        }
      }
    })
  }

  // The detail can come back saying nobody holds the role after the list said
  // otherwise; then there is nothing to reassign and it is a plain delete.
  const handleDeleteReplaceTarget = async () => {
    if (!replaceTarget) return
    try {
      await deleteRole(replaceTarget.id)
      void message.success(t('workspace.orgRoles.messages.deleted'))
      setReplaceTarget(null)
    } catch (error) {
      showApiErrors(error)
    }
  }

  const handleReplaceConfirm = async (replacementRoleId: number) => {
    if (!replaceTarget) return
    try {
      await replaceAndDelete(replaceTarget.id, replacementRoleId)
      void message.success(t('workspace.orgRoles.messages.replaced'))
      setReplaceTarget(null)
    } catch (error) {
      showApiErrors(error)
    }
  }

  const handleSaveMatrix = async (updates: RolePermissionUpdate[]) => {
    try {
      // Sequential on purpose: a workspace has a handful of roles, and one
      // failed PATCH must not leave the rest silently half-applied.
      for (const update of updates) {
        await updateRolePermissions(update.roleId, update.permissionIds)
      }
      void message.success(t('workspace.orgRoles.messages.permissionsUpdated'))
    } catch (error) {
      showApiErrors(error)
    }
  }

  return (
    <WorkspacePageShell
      sectionTitle={t('workspace.orgRoles.sectionTitle')}
      sectionDescription={t('workspace.orgRoles.sectionDescription')}
      titleExtra={
        <RolesKpiChips
          workspaceRoleCount={roles.length}
          customRoleCount={customRoleCount}
          permissionsCount={permissions.length}
        />
      }
    >
      <Flex vertical gap={14}>
        <Tabs
          activeKey={tab}
          onChange={(k) => queryState.set('tab', k as TabKey)}
          tabBarStyle={{ marginBottom: 0 }}
          items={[
            {
              key: 'roles',
              label: (
                <Flex align="center" gap={6}>
                  <ShieldCheck size={14} />
                  {t('workspace.orgRoles.tabs.roles')}
                  <TabCountTag value={roles.length} />
                </Flex>
              )
            },
            {
              key: 'permissions',
              label: (
                <Flex align="center" gap={6}>
                  <Key size={14} />
                  {t('workspace.orgRoles.tabs.permissions')}
                  <TabCountTag value={permissions.length} />
                </Flex>
              )
            }
          ]}
        />

        {tab === 'roles' ? (
          <>
            <Card size="small" variant="outlined">
              <Flex justify="space-between" align="center" gap={8} wrap="wrap">
                <Space vertical size={2}>
                  <Typography.Title level={5} style={{ margin: 0 }}>
                    {t('workspace.orgRoles.matrix.title')}
                  </Typography.Title>
                  <Typography.Text type="secondary">
                    {t('workspace.orgRoles.matrix.description')}
                  </Typography.Text>
                </Space>
                {canManageRoles ? (
                  <Button
                    type="primary"
                    icon={<Plus size={16} />}
                    onClick={() =>
                      setRoleDrawer({ open: true, mode: 'create' })
                    }
                  >
                    {t('workspace.orgRoles.addRole')}
                  </Button>
                ) : null}
              </Flex>
            </Card>

            {isLoadingRoles || isLoadingPermissions ? (
              <Loading />
            ) : (
              <WorkspaceRolesMatrix
                permissions={permissions}
                roles={roles}
                saving={isMutating}
                canManage={canManageRoles}
                onEditRole={(role) =>
                  setRoleDrawer({ open: true, mode: 'edit', role })
                }
                onDeleteRole={handleDeleteRole}
                onSave={(updates) => void handleSaveMatrix(updates)}
              />
            )}
          </>
        ) : (
          <PermissionsCatalogTab permissions={permissions} roles={roles} />
        )}

        <RoleDrawer
          open={roleDrawer.open}
          mode={roleDrawer.open ? roleDrawer.mode : 'create'}
          role={
            roleDrawer.open && roleDrawer.mode === 'edit'
              ? roleDrawer.role
              : undefined
          }
          saving={isMutating}
          onClose={() => setRoleDrawer({ open: false })}
          onSubmit={handleSubmitRole}
          onDelete={(role) => {
            setRoleDrawer({ open: false })
            handleDeleteRole(role)
          }}
        />

        <ArchiveRoleModal
          open={replaceTarget != null}
          roleName={replaceTarget?.name ?? ''}
          memberCount={
            replaceTargetDetail?.users_count ?? replaceTarget?.users_count ?? 0
          }
          availableRoles={replacementCandidates.map((r) => ({
            id: r.id,
            name: r.name
          }))}
          confirmLoading={isMutating}
          onConfirm={handleReplaceConfirm}
          onDeleteOnly={handleDeleteReplaceTarget}
          onCancel={() => setReplaceTarget(null)}
        />
      </Flex>
    </WorkspacePageShell>
  )
}

export const WorkspaceRolesPage = () => <RolesContent />
